import React from 'react';
import { Link } from 'react-router-dom';
import { EngagementRate, PlatformLogo, ScoreBadge } from '@monorepo/common/components/ui';
import {
  compact, pct, platformLabel, platformRoute, signed,
} from '@monorepo/common/utils/platforms';
import { SORTS } from './AccountsSort';

/**
 * The same accounts as a table.
 *
 * The card grid is for reading one account at a time; this is for comparing
 * many of them down a single column, which is the thing a grid cannot do
 * however it is arranged. Fifteen accounts is where it stops being optional.
 *
 * It scrolls SIDEWAYS on a phone rather than dropping columns, because a
 * column that disappears below `sm` is one nobody can sort by on the device
 * most of this gets read on. The account name is sticky so the row stays
 * identifiable while the numbers scroll under it.
 *
 * ### Three columns, six metrics
 *
 * Six columns at 640px minimum meant a phone showed the account and about one
 * and a half numbers; the rest was a horizontal scroll nobody makes. Each
 * column now carries a PAIR of figures that are read together anyway —
 * followers with its change, engagement with reach, score with how many posts
 * are breaking out — stacked in one cell. That is ~200px narrower, so a 390px
 * screen shows the account and two full columns before any scrolling.
 *
 * Both halves of a pair stay sortable: the header holds two buttons, not one,
 * so nothing that used to be a column has become unreachable. They drive the
 * same state as the select above the table, so the two cannot drift.
 */
const COLUMNS = [
  {
    key: 'followers',
    parts: [
      { label: 'Followers', sort: 'followers' },
      { label: 'Change', sort: 'growth_abs' },
    ],
  },
  {
    key: 'engagement',
    parts: [
      { label: 'Net eng.', sort: 'engagement' },
      { label: 'Views/post', sort: 'reach' },
    ],
  },
  {
    key: 'score',
    parts: [
      { label: 'Score', sort: 'score' },
      { label: 'Breaking', sort: 'breaking_out' },
    ],
  },
];

const Change = ({ account: a }) => {
  if (a.followers_delta === null || a.followers_delta === undefined) {
    return <span className="text-ink-subtle">—</span>;
  }
  const dir = a.followers_delta > 0 ? 'up' : a.followers_delta < 0 ? 'down' : 'flat';
  return (
    <span className={
      dir === 'up' ? 'text-success' : dir === 'down' ? 'text-danger' : 'text-ink-subtle'
    }>
      <span aria-hidden="true">{dir === 'up' ? '▴' : dir === 'down' ? '▾' : '–'}</span>
      {signed(a.followers_delta)}
      {a.followers_pct != null && (
        <span className="ml-1 text-ink-subtle">{pct(a.followers_pct)}</span>
      )}
    </span>
  );
};

/** One sortable header label. Two of these share a header cell. */
const SortButton = ({ part, sort, onSort, secondary }) => {
  const active = sort === part.sort;
  const full = SORTS.find((s) => s.value === part.sort)?.label || part.label;
  return (
    <button
      type="button"
      onClick={() => onSort(part.sort)}
      title={`Sort by ${full.toLowerCase()}`}
      className={[
        'inline-flex items-center gap-0.5 whitespace-nowrap',
        'tracking-[0.08em] transition-colors duration-[120ms]',
        secondary ? 'text-[10px]' : 'text-caption-sm font-medium uppercase',
        active ? 'text-ink' : 'text-ink-subtle hover:text-ink',
      ].join(' ')}
    >
      {part.label}
      {/* Only the active sort carries a marker. An arrow on every header reads
          as "all of these are sorted". */}
      <span aria-hidden="true" className={active ? '' : 'hidden'}>▾</span>
    </button>
  );
};

export const AccountsTable = ({ accounts, sort, onSort }) => (
  // The wrapper scrolls, not the page. `min-w-0` is what lets it: a grid or
  // flex child refuses to shrink below its content without it, so the table
  // would widen the whole page instead of scrolling inside this box.
  <div className="min-w-0 overflow-x-auto rounded-card border border-line bg-surface">
    <table className="w-full min-w-[440px] border-collapse text-body-sm">
      <thead>
        <tr className="border-b border-line text-left">
          <th scope="col"
              className="sticky left-0 z-10 bg-surface px-2.5 py-2 text-caption-sm font-medium uppercase tracking-[0.08em] text-ink-subtle sm:px-3">
            Account
          </th>
          {COLUMNS.map((col) => (
            <th key={col.key} scope="col"
                aria-sort={col.parts.some((p) => p.sort === sort) ? 'descending' : 'none'}
                className="px-2.5 py-1.5 text-right sm:px-3">
              {/* The pair is stacked, not side by side: side by side these are
                  two words competing for the widest column in the table, which
                  is the width problem this change exists to remove. */}
              <span className="flex min-h-[var(--tap-target)] flex-col items-end justify-center gap-0.5 sm:min-h-0">
                <SortButton part={col.parts[0]} sort={sort} onSort={onSort} />
                <SortButton part={col.parts[1]} sort={sort} onSort={onSort} secondary />
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {accounts.map((a) => (
          <tr key={`${a.platform}-${a.account_id}`}
              className="border-b border-line last:border-0">
            <th scope="row" className="sticky left-0 z-10 bg-surface px-2.5 py-1.5 text-left font-normal sm:px-3 sm:py-2">
              <Link
                to={a.route || platformRoute(a.platform, a.account_id)}
                className="flex min-h-[var(--tap-target)] min-w-0 items-center gap-1.5 text-ink hover:underline sm:min-h-0"
              >
                {/* The mark, not a coloured dot. The platform name still rides
                    alongside it as the accessible label, because identity must
                    never be carried by a glyph alone either. */}
                <PlatformLogo platform={a.platform} label={platformLabel(a.platform)}
                              className="size-3.5 shrink-0 text-ink-subtle" />
                <span className="min-w-0">
                  <span className="block truncate font-medium">
                    {a.name || (a.username ? `@${a.username}` : platformLabel(a.platform))}
                  </span>
                  {a.username && a.name && (
                    <span className="block truncate text-caption-sm text-ink-subtle">@{a.username}</span>
                  )}
                </span>
              </Link>
            </th>

            <td className="px-2.5 py-1.5 text-right tabular-nums sm:px-3 sm:py-2">
              <span className="block text-ink">
                {a.followers != null ? compact(a.followers) : '—'}
              </span>
              <span className="block text-caption-sm"><Change account={a} /></span>
            </td>

            <td className="px-2.5 py-1.5 text-right tabular-nums sm:px-3 sm:py-2">
              <span className="block text-ink"><EngagementRate rate={a.net_engagement_rate} /></span>
              <span className="block text-caption-sm text-ink-subtle">
                {a.avg_views_per_post != null ? `${compact(a.avg_views_per_post)}/post` : '—'}
              </span>
            </td>

            <td className="px-2.5 py-1.5 text-right sm:px-3 sm:py-2">
              <ScoreBadge score={a.score} />
              {/* Zero is an answer. This read `count > 0 ? count : '—'`, so
                  "none are breaking out" and "we have no data" were the same
                  em dash — and they are different facts. */}
              <span className="mt-0.5 block text-caption-sm tabular-nums text-ink-subtle">
                {a.breaking_out_count ?? '—'} breaking
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default AccountsTable;
